import { X, CheckCircle } from 'lucide-react';

interface DetalleDiaConfirmar {
  recetaNombre: string;
  cantidad: number;
}

interface ConfirmarPedidoDiaModalProps {
  isOpen: boolean;
  fecha: string;
  nombreDia: string;
  detalles: DetalleDiaConfirmar[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Modal de confirmación para confirmar pedido de un día específico
 */
const ConfirmarPedidoDiaModal: React.FC<ConfirmarPedidoDiaModalProps> = ({
  isOpen,
  fecha,
  nombreDia,
  detalles,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  // Calcular total de platos
  const totalPlatos = detalles.reduce((sum, d) => sum + d.cantidad, 0);

  // Formatear fecha
  const formatearFecha = (fechaStr: string) => {
    try {
      const [año, mes, dia] = fechaStr.split('-');
      return `${dia}/${mes}/${año}`;
    } catch {
      return fechaStr;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirmar Pedido
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {nombreDia} - {formatearFecha(fecha)}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Mensaje */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              ¿Está seguro de confirmar el pedido para este día?
              <br />
              Una vez confirmado, el pedido quedará en estado{' '}
              <strong>"Solicitado"</strong> y será enviado a cocina.
            </p>
          </div>

          {/* Resumen */}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">
              Resumen del Pedido
            </h4>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Total de platos:
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {totalPlatos} porciones
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Cantidad de recetas:
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {detalles.length} recetas
                </span>
              </div>
            </div>
          </div>

          {/* Detalles */}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">
              Detalle por Receta
            </h4>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Receta
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cantidad
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {detalles.map((detalle, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {detalle.recetaNombre}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white font-medium">
                        {detalle.cantidad}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Advertencia */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Nota:</strong> Podrá modificar este pedido más tarde
              presionando el botón "Modificar", pero deberá confirmar nuevamente
              después de hacer los cambios.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 p-6 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Confirmando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Confirmar Pedido
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmarPedidoDiaModal;
